import { atom, useAtom } from 'jotai'
import { MenuPage } from './functionMenu.store'

import { useRef } from 'react'
import {
  getConversation,
  getConversationDetail,
  delConversation,
} from '../api/conversation'
import { testAuthApi } from '@/api/modules/login.ts'
/**
 * 聊天消息的数据结构
 */
export interface ChatMessage {
  role: 'USER' | 'ASSISTANT' | 'SYSTEM'
  content: string
  createTime: string
  messageId: string
  isError: boolean
  onlineContent?: string
  thinkContent?: string
  timestamp?: number
  isFinished?: boolean
  images?: any[]
  medium?: any
}

/**
 * AI模型能力配置
 */
export interface AiCapabilities {
  deepThink: boolean
  onlineSearch: boolean
}

/**
 * 对话会话的数据结构
 */
export interface Conversation {
  conversationId: string
  title: string
  type: MenuPage
  messages: ChatMessage[]
  createTime?: string
  extConfig?: any
}

/**
 * 基础消息接口，定义所有消息类型必须包含的字段
 */
export interface BaseMessage {
  role: 'USER' | 'ASSISTANT' | 'SYSTEM'
  content: string
  isError: boolean
  createTime: string
  messageId: string
  loading?: boolean
}

/**
 * 将Blob对象转换为DataURL格式
 * @param blob - 要转换的Blob对象
 * @returns 转换后的DataURL字符串
 */
const blobToDataUrl = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

/**
 * 处理文件数据，将blob转换为dataUrl
 * 起初是专为图片生成页面使用，后来在其他文件生成的时候也有用到
 * TODO: 这里如果有时间的话，可以考虑将这个函数抽离出来，做成一个通用的/语义更好的函数
 * @param conversations - 要处理的会话列表
 * @returns 处理后可以存储的会话列表
 */
const prepareConversationsForStorage = async (
  conversations: Conversation[]
): Promise<Conversation[]> => {
  const processedConversations = JSON.parse(
    JSON.stringify(conversations)
  ) as Conversation[]

  // 处理每个会话
  for (const conversation of processedConversations) {
    // 处理每条消息
    for (const message of conversation.messages) {
      // 处理图片
      if (message.images) {
        for (let i = 0; i < message.images.length; i++) {
          const image = message.images[i]
          // 查找原始会话中对应的图片，因为 JSON.parse/stringify 会丢失 Blob 对象
          const originalConversation = conversations.find(
            (c) => c.conversationId === conversation.conversationId
          )
          const originalMessage = originalConversation?.messages.find(
            (m) => m.timestamp === message.timestamp
          )
          const originalImage = originalMessage?.images?.[i]

          // 如果原始图片有blob但没有dataUrl，转换并保存
          if (
            originalImage?.blob &&
            originalImage.blob instanceof Blob &&
            !originalImage.dataUrl
          ) {
            try {
              // 为了避免异步问题，我们这里不等待转换完成
              // 只在下次保存时才会包含dataUrl
              blobToDataUrl(originalImage.blob)
                .then((dataUrl) => {
                  // 更新原始对象的 dataUrl
                  originalImage.dataUrl = dataUrl
                })
                .catch((e) => {
                  console.error('转换文件失败', e)
                })
            } catch (e) {
              console.error('转换文件失败', e)
            }
          }

          // 移除blob字段，太大了，不适合存储在localStorage
          delete image.blob
        }
      }
    }
  }

  return processedConversations
}

const conversationsAtom = atom<Conversation[]>([])

const activeConversationAtom = atom<Conversation | null>(null)
const aiCapabilitiesAtom = atom<AiCapabilities>({
  deepThink: false,
  onlineSearch: false,
})

/**
 * 用于管理对话上下文的React Hook
 * 提供对话的创建、更新、删除和读取功能
 * 自动将对话数据持久化到localStorage
 * @returns 包含对话状态和操作方法的对象
 */
export const useConversationContext = () => {
  const [conversations, setConversations] = useAtom(conversationsAtom)
  const [activeConversation, setActiveConversation] = useAtom(
    activeConversationAtom
  )
  const [aiCapabilities, setAiCapabilities] = useAtom(aiCapabilitiesAtom)

  // 跟踪是否有待保存的更改
  const pendingSaveRef = useRef<number | null>(null)

  const fetchConversations = async () => {
    try {
      const res = await getConversation()
      setConversations(res.data)
      return res.data
    } catch (error) {
      console.error('加载会话失败', error)
    }
  }

  // 当 conversations 变化时保存到 localStorage
  // useEffect(() => {
  //   // 使用防抖来减少保存频率
  //   if (pendingSaveRef.current) {
  //     clearTimeout(pendingSaveRef.current)
  //   }

  //   pendingSaveRef.current = setTimeout(async () => {
  //     try {
  //       // 在保存前处理数据
  //       const processedConversations = await prepareConversationsForStorage(
  //         conversations
  //       )
  //       localStorage.setItem(
  //         STORAGE_KEY,
  //         JSON.stringify(processedConversations)
  //       )
  //     } catch (error) {
  //       console.error('Failed to save conversations to localStorage:', error)
  //     }
  //   }, 500)

  //   return () => {
  //     if (pendingSaveRef.current) {
  //       clearTimeout(pendingSaveRef.current)
  //     }
  //   }
  // }, [conversations])

  /**
   * 更新特定AI能力的状态
   * @param key - 要更新的能力名称
   * @param value - 是否启用该能力
   */
  const updateCapability = (key: keyof AiCapabilities, value: boolean) => {
    let newCapabilities = { ...aiCapabilities, [key]: value || false }
    setAiCapabilities(newCapabilities)

    if (activeConversation)
      updateActiveConversation({
        ...activeConversation,
        extConfig: newCapabilities,
      })
  }

  /**
   * 切换特定AI能力的状态
   * @param key - 要切换的能力名称
   */
  const toggleCapability = (key: keyof AiCapabilities) => {
    const currentValue = aiCapabilities[key]
    updateCapability(key, !currentValue)
  }

  /**
   * 创建新的对话会话
   * @param type - 对话类型
  //  * @param items - 初始消息项
   * @param content - 用户输入内容，可选，用于生成标题
   * @returns 新创建的对话会话对象
   */
  const createConversation = (
    type: MenuPage,
    // items: GetProp<typeof Bubble.List, "items">,
    content?: string,
    extConfig?: any
  ) => {
    const timestamp = Date.now()
    let title = ''

    if (content && content.trim()) {
      const maxLength = 15
      title =
        content.trim().length > maxLength
          ? `${content.trim().substring(0, maxLength)}...`
          : content.trim()
    } else title = `新对话`

    const newConversation: Conversation = {
      conversationId: timestamp.toString(),
      title,
      type,
      messages: [],
      extConfig: extConfig,
    }
    setConversations([newConversation, ...conversations])
    setActiveConversation(newConversation)
    return newConversation
  }

  /**
   * 删除指定ID的对话会话
   * @param conversationId - 要删除的对话ID
   */
  const deleteConversation = async (conversationId: string) => {
    try {
      await delConversation(conversationId)

      setConversations(
        conversations.filter((conv) => conv.conversationId !== conversationId)
      )

      if (activeConversation?.conversationId === conversationId) {
        setActiveConversation(null)
      }
    } catch (e) {
      console.error('删除会话失败', e)
    }
    // setConversations(conversations.filter((conv) => conv.id !== conversationId))
    // if (activeConversation?.id === conversationId) {
    //   setActiveConversation(null)
    // }
  }

  /**
   * 更新指定的对话会话
   * @param conversation - 要更新的对话会话对象
   */
  const updateActiveConversation = (conversation: Conversation) => {
    // 更新 conversations 数组
    const updatedConversations = conversations.map((conv) =>
      conv.conversationId === conversation.conversationId ? conversation : conv
    )

    setConversations(updatedConversations)

    // 如果是当前活跃的对话，也更新 activeConversation
    if (activeConversation?.conversationId === conversation.conversationId) {
      setActiveConversation(conversation)
    }
  }

  /**
   * 更新指定的对话会话，但不更新localStorage
   * @param conversation - 要更新的对话会话对象
   */
  const updateConversationWithoutLocalStorage = (
    conversation: Conversation
  ) => {
    // 更新 conversations 数组
    const updatedConversations = conversations.map((conv) =>
      conv.conversationId === conversation.conversationId ? conversation : conv
    )

    setConversations(updatedConversations)

    // 如果是当前活跃的对话，也更新 activeConversation
    if (activeConversation?.conversationId === conversation.conversationId) {
      setActiveConversation(conversation)
    }
  }

  /**
   * 更新对话的标题
   * @param conversationId - 要更新标题的对话ID
   * @param newTitle - 新的标题
   */
  const updateConversationTitle = (
    conversationId: string,
    newTitle: string
  ) => {
    if (!newTitle.trim()) return // 不允许空标题

    const updatedConversations = conversations.map((conv) =>
      conv.conversationId === conversationId
        ? { ...conv, title: newTitle }
        : conv
    )

    setConversations(updatedConversations)

    // 如果是当前活跃的对话，也更新 activeConversation
    if (activeConversation?.conversationId === conversationId) {
      setActiveConversation({ ...activeConversation, title: newTitle })
    }
  }

  /**
   * 选择指定ID的对话作为当前活跃对话
   * @param conversationId - 要选择的对话ID
   */
  const lastConversationIdRef = useRef<string | null>(null)
  const chooseActiveConversation = async (conversationId: string) => {
    if (lastConversationIdRef.current === conversationId) {
      // 已经是同一个会话，不用重复请求
      return
    }
    lastConversationIdRef.current = conversationId

    try {
      const conversationData = await fetchConversations()
      const findConversation = conversationData.find(
        (conv: Conversation) => conv.conversationId === conversationId
      )
      if (!findConversation) return
      if (findConversation?.extConfig) {
        setAiCapabilities({
          deepThink: findConversation.extConfig.deepThink,
          onlineSearch: findConversation.extConfig.onlineSearch,
        })
      }
      const res: any = await getConversationDetail(conversationId)
      setActiveConversation({
        ...findConversation,
        messages: res.data,
      })
    } catch (error) {
      console.error('获取会话详情失败', error)
    }
  }

  /**
   * 清除当前活跃的对话
   */
  const clearActiveConversation = () => {
    setActiveConversation(null)
  }

  /**
   * 向会话中添加助手回复消息
   * @param assistantContent - 消息内容
   * @param role - 消息角色
   * @param isError - 是否为错误消息
   * @param userTimestamp - 用户消息的时间戳
   * @param userMessage - 用户消息对象
   * @param baseMessages - 可选的基础消息列表，用于避免状态更新延迟问题
   */
  const appendAssistantMessage = <T extends BaseMessage>(
    assistantContent: any,
    role: 'ASSISTANT',
    isError: boolean = false,
    userTimestamp: string,
    userMessage: T,
    baseMessages?: T[]
  ) => {
    if (!activeConversation) return

    // 使用传入的baseMessages或当前的activeConversation.messages
    const currentMessages = baseMessages || activeConversation.messages

    const messagesWithUser = currentMessages.some(
      (msg) => msg.messageId == userTimestamp && msg.role == 'USER'
    )
      ? currentMessages
      : [...currentMessages, userMessage]
    // 查找用户消息的索引
    const userMessageIndex = messagesWithUser.findIndex(
      (msg) => msg.messageId == userTimestamp && msg.role == 'USER'
    )
    if (userMessageIndex === -1) {
      console.error('找不到对应的用户消息')
      return
    }

    // 查找该用户消息之后的第一个assistant消息
    const existingAssistantIndex = messagesWithUser.findIndex(
      (msg, index) => index > userMessageIndex && msg.role === 'ASSISTANT'
    )

    let finalMessages: T[]

    if (existingAssistantIndex !== -1) {
      // 更新现有的assistant消息
      finalMessages = messagesWithUser.map((msg, index) =>
        index === existingAssistantIndex
          ? ({
            ...msg,
            ...assistantContent,
            isError,
          } as T)
          : msg
      ) as T[]
      // .filter((msg) => !(msg as BaseMessage).isError)
    } else {
      // 创建新的assistant消息
      const assistantTimestamp = String(Date.now())
      const assistantMessage = {
        role,
        ...assistantContent,
        createTime: assistantTimestamp,
        isError,
        messageId: assistantTimestamp,
      }

      finalMessages = messagesWithUser
        .filter((msg) => !(msg as BaseMessage).isError)
        .concat([assistantMessage]) as T[]
    }

    if (isError) {
      console.log('更新错误后的消息列表:', finalMessages)
    }

    updateActiveConversation({
      ...activeConversation,
      messages: finalMessages,
    })
  }

  /**
   * 处理消息发送的完整流程
   * @param text - 消息文本
   * @param sendRequest - 发送请求的函数
   * @param createMessage - 创建消息对象的函数
   * @param setLoading - 设置加载状态的函数
   * @param setInputContent - 设置输入内容的函数
   */
  const processSendMessage = async <T extends BaseMessage>({
    text,
    imageUrls,
    docUrls,
    sendRequest,
    createMessage,
    setLoading,
    setInputContent,
  }: {
    text: string
    imageUrls: string[]
    docUrls: any[]
    sendRequest: (
      text: string,
      timestamp: string,
      message: T,
      messages: T[]
    ) => Promise<void>
    createMessage: (text: string, timestamp: string, imageUrls: string[], docUrls: any[]) => T
    setLoading: (loading: boolean) => void
    setInputContent: (content: string) => void
  }) => {
    if (!text.trim() || !activeConversation) return
    console.error('开始处理发送消息:')
    setLoading(true)
    const authRes = await testAuthApi().catch(() => {
      setLoading(false)
    })
    if (authRes.code === 402) {
      return;
    }
    setInputContent('') // 清空输入框

    const userTimestamp = String(Date.now())
    const userMessage = createMessage(text, userTimestamp, imageUrls, docUrls)
    const assistantTimestamp = String(Date.now() + 1)
    const assistantMessage = {
      role: 'ASSISTANT',
      content: '',
      createTime: assistantTimestamp,
      isError: false,
      isFinished: false,
      messageId: assistantTimestamp,
    }
    const finalMessages = [
      ...activeConversation.messages,
      userMessage,
      assistantMessage,
    ]
    updateActiveConversation({
      ...activeConversation,
      messages: finalMessages as T[],
    })

    try {
      await sendRequest(text, userTimestamp, userMessage, finalMessages as T[])
    } catch (error) {
      console.error('处理请求错误:', error)
      appendAssistantMessage(
        '抱歉，处理您的请求时出现错误。',
        'ASSISTANT',
        true,
        userTimestamp,
        userMessage
      )
    } finally {
      setLoading(false)
    }
  }

  const processReloadMessage = async <T extends BaseMessage>({
    messageId,
    sendRequest,
    setLoading,
    setInputContent,
  }: {
    messageId: string
    sendRequest: (
      text: string,
      messageId: string,
      message: T,
      messages: T[]
    ) => Promise<void>
    setLoading: (loading: boolean) => void
    setInputContent: (content: string) => void
  }) => {
    if (!messageId || !activeConversation) return
    setLoading(true)
    const authRes = await testAuthApi().catch(() => {
      setLoading(false)
    })
    if (authRes.code === 402) {
      return; // 权限不足，直接拦截，不开启 SSE
    }
    setInputContent('')

    const messageIndex = activeConversation.messages.findIndex(
      (msg) => msg.messageId === messageId
    )

    if (messageIndex === -1) return

    const userMessage = activeConversation.messages
      .slice(0, messageIndex)
      .reverse()
      .find((msg) => msg.role === 'USER')

    if (!userMessage) return

    const remainingMessages = deleteMessageAndAfter(messageId)

    const assistantTimestamp = String(Date.now() + 1)
    const assistantMessage = {
      role: 'ASSISTANT',
      content: '',
      createTime: assistantTimestamp,
      isError: false,
      isFinished: false,
      messageId: assistantTimestamp,
    }
    const finalMessages = [...remainingMessages, assistantMessage]
    updateActiveConversation({
      ...activeConversation,
      messages: finalMessages,
    })
    try {
      await sendRequest(
        userMessage.content,
        userMessage.messageId,
        userMessage,
        finalMessages
      )
    } catch (error) {
      console.error('重新生成消息错误:', error)
      appendAssistantMessage(
        '抱歉，重新生成回复时出现错误。',
        'ASSISTANT',
        true,
        userMessage.messageId,
        userMessage,
        remainingMessages
      )
    } finally {
      setLoading(false)
    }
  }

  /**
   * 删除指定时间戳的消息及其之后的所有消息
   * @param messageTimestamp - 要删除的消息时间戳
   * @returns 删除后的消息列表
   */
  const deleteMessageAndAfter = (messageId: string) => {
    if (!activeConversation) return []

    const updatedMessages = activeConversation.messages.filter(
      (msg) => Number(msg.messageId) < Number(messageId)
    )

    updateActiveConversation({
      ...activeConversation,
      messages: updatedMessages,
    })

    return updatedMessages
  }

  /**
   * 更新指定消息的内容
   * @param messageTimestamp - 要更新的消息时间戳
   * @param newContent - 新的消息内容
   */
  const updateMessageContent = (
    messageTimestamp: number,
    newContent: string
  ) => {
    if (!activeConversation) return

    const updatedMessages = activeConversation.messages.map((msg) =>
      msg.timestamp === messageTimestamp ? { ...msg, content: newContent } : msg
    )

    updateActiveConversation({
      ...activeConversation,
      messages: updatedMessages,
    })
  }

  return {
    conversations,
    activeConversation,
    aiCapabilities,
    updateCapability,
    toggleCapability,
    createConversation,
    deleteConversation,
    updateActiveConversation,
    updateConversationWithoutLocalStorage,
    chooseActiveConversation,
    clearActiveConversation,
    updateConversationTitle,
    appendAssistantMessage,
    processSendMessage,
    processReloadMessage,
    deleteMessageAndAfter,
    updateMessageContent,
    fetchConversations,
  }
}
